import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Typography,
  Grid,
  Chip,
  Link,
  MenuItem,
} from '@mui/material';
import PushPinOutlined from '@mui/icons-material/PushPinOutlined';
import ManageSearchRounded from '@mui/icons-material/ManageSearchRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import {
  AccountPageWrapper,
  StyledTabs,
  StyledTab,
  StyledTextField,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  StyledDialog,
  DeleteAccountWarningDialog,
  ServiceRequestPrimaryButton,
  ServiceRequestSecondaryButton,
  ServiceRequestsSubTabs,
  ServiceRequestsSubTab,
} from './AccountPage.styled';
import {
  ACCOUNT_EMAIL_CHANGE_HELPER_TEXT,
  teamContactMailtoHref,
} from '../../utils/teamContact';
import { useAuth } from '../../context/AuthContext';
import { accountService } from '../../services/api/accountService';
import { phoneDigitsForApi } from '../../utils/phoneInputMask';
import { toPersonNameCase } from '../../utils/normalizePersonName';
import { aiService } from '../../services/api/aiService';
import { serviceWizardSearchRequestService } from '../../services/api/serviceWizardSearchRequestService';
import { fetchBusinessNamesByIds } from '../../utils/fetchBusinessNamesByIds';
import { navigateToStorefront } from '../../utils/storefrontPath';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import NkySnackbarAlert from '../common/NkySnackbarAlert/NkySnackbarAlert';
import {
  fingerprintWizardState,
  setPersistedWizardSearchRequestId,
  getLastWizardSearchRequestId,
} from '../../utils/wizardClientSession';
import { buildResultsPathFromSearchRequest } from '../../utils/serviceSearch';
import { mapServiceWizardSearchRequestToWizardState } from '../../hooks/useWizardStateFromSearchRequestApi';
import {
  ALL_TRADES,
  findSubserviceAcrossTrades,
} from '../../constants/mockData/allTrades';
import WizardRequestSummaryDialog from '../common/WizardRequestSummaryDialog/WizardRequestSummaryDialog';
import CustomServiceRequestDetailDialog from '../common/CustomServiceRequestDetailDialog/CustomServiceRequestDetailDialog';
import ProviderCard from '../ProviderCard/ProviderCard';
import { BusinessCard } from '../OurPartners/OurPartners.styled';
import {
  isCustomServiceWizardRequest,
  isWizardFlowServiceRequest,
} from '../../utils/serviceWizardRequestKind';
import {
  CUSTOM_SERVICE_LABEL,
  resolveServiceTypeDisplayLabel,
} from '../../utils/serviceTypeDisplay';

function AccountPage() {
  const [tabValue, setTabValue] = useState(0);
  const [userData, setUserData] = useState(null);
  const [savedProviders, setSavedProviders] = useState([]);
  const [recommendationHistory, setRecommendationHistory] = useState([]);
  const [savedProvidersLoading, setSavedProvidersLoading] = useState(false);
  const [hasFetchedSavedProviders, setHasFetchedSavedProviders] = useState(false);
  const [recommendationHistoryLoading, setRecommendationHistoryLoading] =
    useState(false);
  const [hasFetchedRecommendationHistory, setHasFetchedRecommendationHistory] =
    useState(false);
  const [wizardServiceRequests, setWizardServiceRequests] = useState([]);
  const [wizardRequestsLoading, setWizardRequestsLoading] = useState(false);
  const [hasFetchedWizardRequests, setHasFetchedWizardRequests] = useState(false);
  const [selectedWizardRequest, setSelectedWizardRequest] = useState(null);
  const [selectedCustomRequest, setSelectedCustomRequest] = useState(null);
  const [serviceRequestsSubTab, setServiceRequestsSubTab] = useState(0);
  const [businessNameById, setBusinessNameById] = useState({});
  /** Mirrors `sessionStorage` active wizard search id (for “active” badge on cards). */
  const [activeSessionSearchId, setActiveSessionSearchId] = useState(() =>
    getLastWizardSearchRequestId(),
  );
  const [sessionSearchFeedback, setSessionSearchFeedback] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedBusinessesError, setSavedBusinessesError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    businessId: null,
    businessName: '',
  });
  const [removeSaving, setRemoveSaving] = useState(false);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState(null);
  const [saveToast, setSaveToast] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const closeSaveToast = () => {
    setSaveToast((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (tabValue !== 1) return undefined;
    if (hasFetchedSavedProviders) return undefined;

    let cancelled = false;

    const fetchSavedProviders = async () => {
      setSavedBusinessesError(null);
      setSavedProvidersLoading(true);

      try {
        const savedProvidersData = await accountService.getSavedBusinesses(
          true,
          true,
        );
        if (!cancelled) {
          setSavedProviders(
            Array.isArray(savedProvidersData) ? savedProvidersData : [],
          );
          setHasFetchedSavedProviders(true);
        }
      } catch (err) {
        console.error('Error fetching saved providers:', err);
        if (!cancelled) {
          setSavedProviders([]);
          setSavedBusinessesError(
            'Saved businesses are temporarily unavailable. Please try again shortly.',
          );
        }
      } finally {
        if (!cancelled) {
          setSavedProvidersLoading(false);
        }
      }
    };

    fetchSavedProviders();
    return () => {
      cancelled = true;
    };
  }, [tabValue, hasFetchedSavedProviders]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.Email) return;

      try {
        setIsLoading(true);
        const data = await accountService.getAccount();
        setUserData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching user data: ', err);
        setError('Failed to load user information. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  useEffect(() => {
    if (tabValue !== 3) return undefined;
    const accountId = userData?.AccountId;
    if (!accountId) return undefined;
    if (hasFetchedRecommendationHistory) return undefined;

    const fetchRecommendationHistory = async () => {
      try {
        setRecommendationHistoryLoading(true);
        const history = await aiService.getRecommendationHistory(accountId);
        const sortedHistory = [...history].sort((a, b) => {
          const timeA = a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0;
          const timeB = b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0;
          return timeB - timeA;
        });
        setRecommendationHistory(sortedHistory);
        setHasFetchedRecommendationHistory(true);
      } catch (err) {
        console.error('Error fetching recommendation history:', err);
        setError('Failed to load recommendation history');
      } finally {
        setRecommendationHistoryLoading(false);
      }
    };

    fetchRecommendationHistory();
    return undefined;
  }, [tabValue, userData?.AccountId, hasFetchedRecommendationHistory]);

  useEffect(() => {
    if (tabValue !== 2) return undefined;
    setActiveSessionSearchId(getLastWizardSearchRequestId());
    return undefined;
  }, [tabValue, location.key]);

  useEffect(() => {
    if (tabValue !== 2 || !user?.Email) return undefined;
    if (hasFetchedWizardRequests) return undefined;

    const fetchWizardRequests = async () => {
      try {
        setWizardRequestsLoading(true);
        const rows = await serviceWizardSearchRequestService.listByAccount();
        setWizardServiceRequests(Array.isArray(rows) ? rows : []);
        setHasFetchedWizardRequests(true);
      } catch (err) {
        console.error('Error loading service wizard requests:', err);
      } finally {
        setWizardRequestsLoading(false);
      }
    };

    fetchWizardRequests();
    return undefined;
  }, [tabValue, user?.Email, hasFetchedWizardRequests]);

  const wizardFlowRequests = useMemo(
    () => wizardServiceRequests.filter(isWizardFlowServiceRequest),
    [wizardServiceRequests],
  );

  const customServiceRequests = useMemo(
    () => wizardServiceRequests.filter(isCustomServiceWizardRequest),
    [wizardServiceRequests],
  );

  const resolveBusinessName = useCallback(
    (businessId) => {
      if (!businessId) return 'Business';
      return businessNameById[businessId] || 'Business';
    },
    [businessNameById],
  );

  const mergeBusinessNames = useCallback((namesMap) => {
    if (!namesMap || !Object.keys(namesMap).length) return;
    setBusinessNameById((prev) => ({ ...prev, ...namesMap }));
  }, []);

  useEffect(() => {
    if (tabValue !== 2 || serviceRequestsSubTab !== 1) return undefined;
    const ids = customServiceRequests
      .flatMap((r) => (r.leads || []).map((o) => o.businessId))
      .filter(Boolean);
    const missing = [...new Set(ids)].filter((id) => !businessNameById[id]);
    if (!missing.length) return undefined;

    let cancelled = false;
    fetchBusinessNamesByIds(missing).then((namesMap) => {
      if (!cancelled) mergeBusinessNames(namesMap);
    });
    return () => {
      cancelled = true;
    };
  }, [tabValue, serviceRequestsSubTab, customServiceRequests, businessNameById, mergeBusinessNames]);

  useEffect(() => {
    if (!selectedWizardRequest) return undefined;
    const ids = (selectedWizardRequest.leads || [])
      .map((o) => o.businessId)
      .filter(Boolean);
    const missing = [...new Set(ids)].filter((id) => !businessNameById[id]);
    if (!missing.length) return undefined;

    let cancelled = false;
    fetchBusinessNamesByIds(missing).then((namesMap) => {
      if (!cancelled) mergeBusinessNames(namesMap);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedWizardRequest, businessNameById, mergeBusinessNames]);

  const accountFieldMapping = {
    firstName: 'FirstName',
    lastName: 'LastName',
    phoneNumber: 'PhoneNumber',
    dateOfBirth: 'DateOfBirth',
    email: 'Email',
    preferredContactMethod: 'PreferredContactMethod',
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const propertyName = accountFieldMapping[name] || name;
    setUserData((prevData) => ({
      ...prevData,
      [propertyName]: value,
    }));
  };

  const handleNameBlur = (event) => {
    const { name, value } = event.target;
    if (name !== 'firstName' && name !== 'lastName') return;
    const propertyName = accountFieldMapping[name];
    const normalized = toPersonNameCase(value);
    if (normalized === value) return;
    setUserData((prevData) => ({
      ...prevData,
      [propertyName]: normalized,
    }));
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const { AccountId, Email, ValidEmail, PhoneNumber, PreferredContactMethod, ...rest } =
        userData;

      const phoneDigits = phoneDigitsForApi(PhoneNumber);
      const hasValidPhone = phoneDigits.length >= 10;
      if (
        (PreferredContactMethod === 'phone' || PreferredContactMethod === 'text') &&
        !hasValidPhone
      ) {
        setSaveToast({
          open: true,
          severity: 'error',
          message:
            'Add a phone number before setting preferred contact method to phone or text.',
        });
        return;
      }

      const normalizedFirstName = toPersonNameCase(rest.FirstName);
      const normalizedLastName = toPersonNameCase(rest.LastName);

      const payload = {
        FirstName: normalizedFirstName,
        LastName: normalizedLastName,
        DateOfBirth: rest.DateOfBirth,
        ...(hasValidPhone ? { PhoneNumber: phoneDigits } : {}),
        ...(PreferredContactMethod === 'email' ||
          PreferredContactMethod === 'phone' ||
          PreferredContactMethod === 'text'
          ? { PreferredContactMethod }
          : {}),
      };

      await accountService.updateAccount(payload);
      setUserData((prev) => ({
        ...prev,
        FirstName: normalizedFirstName,
        LastName: normalizedLastName,
      }));
      setSaveToast({
        open: true,
        severity: 'success',
        message: 'Your information was saved successfully.',
      });
    } catch (err) {
      console.error('Error saving user data:', err);
      setSaveToast({
        open: true,
        severity: 'error',
        message: 'Failed to save changes. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChangePassword = () => {
    // alert('Change password functionality will be implemented soon.');
    navigate('/account/change-password');
  };

  const handleRemoveProvider = (businessId, businessName) => {
    setConfirmDialog({
      open: true,
      businessId,
      businessName,
    });
  };

  const handleConfirmRemove = async () => {
    const businessId = confirmDialog.businessId;
    if (!businessId || removeSaving) return;

    const previousProviders = savedProviders;
    const nextProviders = previousProviders.filter(
      (provider) => provider.BusinessId !== businessId,
    );
    const nextIds = nextProviders.map((provider) => provider.BusinessId);

    setRemoveSaving(true);
    setSavedProviders(nextProviders);
    setConfirmDialog({ open: false, businessId: null, businessName: '' });

    try {
      await accountService.editSavedBusinesses(nextIds);
    } catch (err) {
      console.error('Error removing saved business:', err);
      setSavedProviders(previousProviders);
      setSavedBusinessesError(
        'Could not remove that saved business. Please try again.',
      );
    } finally {
      setRemoveSaving(false);
    }
  };

  const handleCancelRemove = () => {
    if (removeSaving) return;
    setConfirmDialog({ open: false, businessId: null, businessName: '' });
  };

  const handleOpenDeleteAccount = () => {
    setDeleteAccountError(null);
    setDeleteAccountDialogOpen(true);
  };

  const handleCloseDeleteAccount = () => {
    if (deleteAccountLoading) return;
    setDeleteAccountDialogOpen(false);
    setDeleteAccountError(null);
  };

  const handleConfirmDeleteAccount = async () => {
    setDeleteAccountLoading(true);
    setDeleteAccountError(null);
    try {
      await accountService.deleteAccount();
      setDeleteAccountDialogOpen(false);
      await logout();
    } catch (err) {
      setDeleteAccountError(
        err?.message ||
        'Unable to delete your account. Please try again or contact support.',
      );
    } finally {
      setDeleteAccountLoading(false);
    }
  };

  const handleCardClick = (business) => {
    navigateToStorefront(navigate, business);
  };

  const handleOpenWizardRequestSummary = (requestRow) => {
    setSelectedWizardRequest(requestRow);
  };

  const handleCloseWizardRequestSummary = () => {
    setSelectedWizardRequest(null);
  };

  const handleOpenCustomRequestDetail = (requestRow) => {
    setSelectedCustomRequest(requestRow);
  };

  const handleCloseCustomRequestDetail = () => {
    setSelectedCustomRequest(null);
  };

  const handleServiceRequestsSubTabChange = (_event, newValue) => {
    setServiceRequestsSubTab(newValue);
    setSessionSearchFeedback(null);
  };

  const handleSetActiveSessionSearch = (requestRow) => {
    const wizardState = mapServiceWizardSearchRequestToWizardState(requestRow);
    if (!wizardState) {
      setSessionSearchFeedback({
        severity: 'warning',
        message:
          'This saved request is missing details and cannot be used for new inquiries. Start a fresh service search instead.',
      });
      return;
    }
    const fp = fingerprintWizardState(wizardState);
    setPersistedWizardSearchRequestId(requestRow.id, fp);
    setActiveSessionSearchId(requestRow.id);
    setSessionSearchFeedback({
      severity: 'success',
      message:
        'This search is now active in this browser. Provider results and storefront inquiries will use it until you run a new service search.',
    });
  };

  const handleGoToProvidersForRequest = (requestRow) => {
    const wizardState = mapServiceWizardSearchRequestToWizardState(requestRow);
    const path = buildResultsPathFromSearchRequest(requestRow);
    if (!wizardState || !path) {
      setSessionSearchFeedback({
        severity: 'warning',
        message:
          'This request cannot be opened. Try another saved search or start a new service search.',
      });
      return;
    }
    const fp = fingerprintWizardState(wizardState);
    setPersistedWizardSearchRequestId(requestRow.id, fp);
    setActiveSessionSearchId(requestRow.id);
    navigate(path);
  };

  const getServiceRequestDisplayNames = (
    serviceType,
    subServiceType,
    targetServiceTitle,
    inquiryKind,
  ) => {
    if (inquiryKind === 'custom') {
      const subserviceName =
        targetServiceTitle?.trim() ||
        (subServiceType && !subServiceType.startsWith('custom-') ? subServiceType : '') ||
        '';
      return { tradeName: CUSTOM_SERVICE_LABEL, subserviceName };
    }

    const trade = ALL_TRADES.find((item) => item.id === serviceType);
    const tradeName = resolveServiceTypeDisplayLabel(serviceType);

    if (!subServiceType) {
      return { tradeName, subserviceName: '' };
    }

    if (targetServiceTitle?.trim()) {
      return { tradeName, subserviceName: targetServiceTitle.trim() };
    }

    const scopedSubservice = trade?.groups
      ?.flatMap((group) => group.subservices || [])
      .find((subservice) => subservice.id === subServiceType);
    const fallbackSubservice = findSubserviceAcrossTrades(subServiceType)?.subservice;
    const subserviceName =
      scopedSubservice?.label || fallbackSubservice?.label || subServiceType;

    return { tradeName, subserviceName };
  };

  if (isLoading) {
    return (
      <AccountPageWrapper>
        <div className="loading-container">
          <NkySpinner />
        </div>
      </AccountPageWrapper>
    );
  }

  if (error) {
    return (
      <AccountPageWrapper>
        <div className="error-container">
          <Alert severity="error">{error}</Alert>
        </div>
      </AccountPageWrapper>
    );
  }

  return (
    <AccountPageWrapper>
      <div className="account-page">
        <section className="account-header" aria-labelledby="account-title">
          <p className="header-eyebrow">Your account</p>
          <h1 className="header-title" id="account-title">
            {userData?.FirstName ? `Hi, ${userData.FirstName}` : 'My account'}
          </h1>
          <p className="header-subtitle">
            Manage your profile, saved pros and service requests.
          </p>
        </section>

        <div className="main-content">
          <div className="tabs-container">
            <StyledTabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="account tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <StyledTab label="Account information" />
              <StyledTab label="Saved pros" />
              <StyledTab label="Service requests" />
              <StyledTab label="Recommendation history" />
            </StyledTabs>

            {/* Account Information Tab Panel */}
            <div
              role="tabpanel"
              hidden={tabValue !== 0}
              id="tabpanel-0"
              aria-labelledby="tab-0"
              className="tab-content"
            >
              {tabValue === 0 && (
                <>
                  <h2 className="tab-title">Account information</h2>
                  <div className="form-grid">
                    <Grid container spacing={3} sx={{ width: '100%' }}>
                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          label="Email"
                          name="email"
                          value={userData?.Email || ''}
                          disabled
                          helperText={ACCOUNT_EMAIL_CHANGE_HELPER_TEXT}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          label="First Name"
                          name="firstName"
                          value={userData?.FirstName || ''}
                          onChange={handleInputChange}
                          onBlur={handleNameBlur}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          label="Last Name"
                          name="lastName"
                          value={userData?.LastName || ''}
                          onChange={handleInputChange}
                          onBlur={handleNameBlur}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          label="Phone Number"
                          name="phoneNumber"
                          value={userData?.PhoneNumber || ''}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          label="Date of Birth"
                          name="dateOfBirth"
                          type="date"
                          value={userData?.DateOfBirth || ''}
                          onChange={handleInputChange}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          select
                          fullWidth
                          label="Preferred contact method"
                          name="preferredContactMethod"
                          value={userData?.PreferredContactMethod || ''}
                          onChange={handleInputChange}
                          SelectProps={{ displayEmpty: true }}
                          InputLabelProps={{ shrink: true }}
                          helperText="How businesses should reach you for service inquiries"
                        >
                          {!userData?.PreferredContactMethod && (
                            <MenuItem value="">
                              <em>Not set</em>
                            </MenuItem>
                          )}
                          <MenuItem value="email">Email</MenuItem>
                          <MenuItem value="phone">Phone call</MenuItem>
                          <MenuItem value="text">Text message</MenuItem>
                        </StyledTextField>
                      </Grid>
                    </Grid>
                  </div>
                  <div className="accountFormActions">
                    <div className="accountFormActionsStart">
                      <PrimaryButton onClick={handleSave}>
                        Save changes
                      </PrimaryButton>
                      <SecondaryButton onClick={handleChangePassword}>
                        Change password
                      </SecondaryButton>
                    </div>
                    <SecondaryButton onClick={logout}>Log out</SecondaryButton>
                  </div>
                </>
              )}
            </div>

            {/* Saved Businesses Tab Panel */}
            <div
              role="tabpanel"
              hidden={tabValue !== 1}
              id="tabpanel-1"
              aria-labelledby="tab-1"
              className="tab-content saved-businesses-tab"
            >
              {tabValue === 1 && (
                <>
                  <h2 className="tab-title">Saved pros</h2>
                  {savedProvidersLoading ? (
                    <div className="loading-container">
                      <NkySpinner />
                    </div>
                  ) : savedBusinessesError ? (
                    <div className="empty-state">
                      <Alert severity="error">{savedBusinessesError}</Alert>
                    </div>
                  ) : savedProviders.length > 0 ? (
                    <Grid container spacing={3} sx={{ width: '100%', mt: 1 }}>
                      {savedProviders.map((provider) => {
                        const city = provider.BusinessAddress?.City;
                        const state = provider.BusinessAddress?.State;
                        const locationDisplay =
                          city && state ? `${city}, ${state}` : 'Northern Kentucky';

                        return (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={provider.BusinessId}
                          >
                            <BusinessCard>
                              <ProviderCard
                                provider={provider}
                                locationDisplay={locationDisplay}
                                isSaved
                                onRemove={handleRemoveProvider}
                                onClick={() => handleCardClick(provider)}
                              />
                            </BusinessCard>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <div className="empty-state">
                      <Alert severity="info">
                        You haven't saved any companies yet. Browse our partners
                        to find companies you're interested in!
                      </Alert>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Recommendation History Tab Panel */}
            <div
              role="tabpanel"
              hidden={tabValue !== 2}
              id="tabpanel-2"
              aria-labelledby="tab-2"
              className="tab-content"
            >
              {tabValue === 2 && (
                <>
                  <h2 className="tab-title">Service requests</h2>

                  <div className="service-requests-subtabs-wrap">
                    <ServiceRequestsSubTabs
                      value={serviceRequestsSubTab}
                      onChange={handleServiceRequestsSubTabChange}
                      aria-label="Service request types"
                    >
                      <ServiceRequestsSubTab
                        label={`Service searches (${wizardFlowRequests.length})`}
                      />
                      <ServiceRequestsSubTab
                        label={`Custom inquiries (${customServiceRequests.length})`}
                      />
                    </ServiceRequestsSubTabs>
                  </div>

                  {serviceRequestsSubTab === 0 ? (
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, maxWidth: 720 }}
                      >
                        Searches from the service wizard. Use <strong>Use for inquiries</strong>{' '}
                        to contact more businesses with the same answers, or{' '}
                        <strong>Matching providers</strong> to view results.
                      </Typography>
                      {sessionSearchFeedback && (
                        <Alert
                          severity={sessionSearchFeedback.severity}
                          onClose={() => setSessionSearchFeedback(null)}
                          sx={{ mb: 2 }}
                        >
                          {sessionSearchFeedback.message}
                        </Alert>
                      )}
                      {wizardRequestsLoading ? (
                        <div className="loading-container">
                          <NkySpinner />
                        </div>
                      ) : wizardFlowRequests.length > 0 ? (
                        <div className="saved-businesses-grid">
                          {wizardFlowRequests.map((req) => {
                            const { tradeName, subserviceName } =
                              getServiceRequestDisplayNames(
                                req.serviceType,
                                req.subServiceType,
                                req.targetServiceTitle,
                                req.inquiryKind,
                              );
                            const created = req.createdAt
                              ? new Date(req.createdAt).toLocaleString()
                              : 'Unknown date';
                            const problem =
                              req.requestSummary?.problemSummary || 'Service request';
                            const outreachCount = req.leads?.length ?? 0;
                            const isActiveSession = activeSessionSearchId === req.id;
                            const canRestore = Boolean(
                              mapServiceWizardSearchRequestToWizardState(req),
                            );
                            return (
                              <div key={req.id} className="business-card-wrapper">
                                <div
                                  className="wizard-request-card"
                                  onClick={() => handleOpenWizardRequestSummary(req)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      handleOpenWizardRequestSummary(req);
                                    }
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  aria-label="Open request summary"
                                >
                                  <div className="wizard-request-header">
                                    <div className="wizard-request-title-wrap">
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {tradeName}
                                        {subserviceName ? ` · ${subserviceName}` : ''}
                                      </Typography>
                                    </div>
                                  </div>
                                  <Typography
                                    variant="subtitle1"
                                    className="wizard-request-created"
                                  >
                                    {created}
                                  </Typography>
                                  <div className="wizard-request-meta-row">
                                    <span className="wizard-request-meta">
                                      {outreachCount} business
                                      {outreachCount === 1 ? '' : 'es'} contacted
                                    </span>
                                    {req.helpRequested ? (
                                      <Chip
                                        size="small"
                                        label="Help requested"
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                      />
                                    ) : null}
                                    {isActiveSession ? (
                                      <Chip
                                        size="small"
                                        label="Active for inquiries"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                      />
                                    ) : null}
                                  </div>
                                  <Typography variant="body2" className="wizard-request-problem">
                                    {problem}
                                  </Typography>
                                </div>
                                <div
                                  className="wizard-request-actions"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ServiceRequestPrimaryButton
                                    variant="contained"
                                    disableElevation
                                    disabled={!canRestore}
                                    startIcon={
                                      <PushPinOutlined sx={{ fontSize: '1.125rem' }} />
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSetActiveSessionSearch(req);
                                    }}
                                  >
                                    Use for inquiries
                                  </ServiceRequestPrimaryButton>
                                  <ServiceRequestSecondaryButton
                                    variant="outlined"
                                    disabled={!canRestore || !req.serviceType}
                                    startIcon={
                                      <ManageSearchRounded sx={{ fontSize: '1.125rem' }} />
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleGoToProvidersForRequest(req);
                                    }}
                                  >
                                    Matching providers
                                  </ServiceRequestSecondaryButton>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="empty-state">
                          <Alert severity="info">
                            When you complete a service search wizard and contact providers, those
                            searches appear here.
                          </Alert>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, maxWidth: 720 }}
                      >
                        Inquiries you sent from a business&apos;s custom service listing. Open a
                        card to view what you submitted and which business received it.
                      </Typography>
                      {wizardRequestsLoading ? (
                        <div className="loading-container">
                          <NkySpinner />
                        </div>
                      ) : customServiceRequests.length > 0 ? (
                        <div className="saved-businesses-grid">
                          {customServiceRequests.map((req) => {
                            const { tradeName, subserviceName } =
                              getServiceRequestDisplayNames(
                                req.serviceType,
                                req.subServiceType,
                                req.targetServiceTitle,
                                req.inquiryKind,
                              );
                            const created = req.createdAt
                              ? new Date(req.createdAt).toLocaleString()
                              : 'Unknown date';
                            const primaryLead = req.leads?.[0];
                            const businessLabel = primaryLead
                              ? resolveBusinessName(primaryLead.businessId)
                              : null;
                            const detailsPreview = req.formData?.details?.trim() || '';
                            return (
                              <div key={req.id} className="business-card-wrapper">
                                <div
                                  className="custom-service-request-card"
                                  onClick={() => handleOpenCustomRequestDetail(req)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      handleOpenCustomRequestDetail(req);
                                    }
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  aria-label="View custom inquiry details"
                                >
                                  <div className="wizard-request-header">
                                    <div className="wizard-request-title-wrap">
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {subserviceName || tradeName}
                                        {subserviceName ? ` · ${tradeName}` : ''}
                                      </Typography>
                                    </div>
                                  </div>
                                  <Typography
                                    variant="subtitle1"
                                    className="wizard-request-created"
                                  >
                                    {created}
                                  </Typography>
                                  <div className="wizard-request-meta-row">
                                    <span className="wizard-request-meta">
                                      {businessLabel
                                        ? `Sent to ${businessLabel}`
                                        : 'Sent to business'}
                                    </span>
                                  </div>
                                  {detailsPreview ? (
                                    <Typography variant="body2" className="wizard-request-problem">
                                      {detailsPreview}
                                    </Typography>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="empty-state">
                          <Alert severity="info">
                            Custom service inquiries from business storefronts appear here after you
                            send them.
                          </Alert>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            <div
              role="tabpanel"
              hidden={tabValue !== 3}
              id="tabpanel-3"
              aria-labelledby="tab-3"
              className="tab-content"
            >
              {tabValue === 3 && (
                <>
                  <h2 className="tab-title">Recommendation history</h2>

                  {recommendationHistoryLoading ? (
                    <div className="loading-container">
                      <NkySpinner />
                    </div>
                  ) : recommendationHistory.length > 0 ? (
                    <div className="saved-businesses-grid">
                      {recommendationHistory.map((sr) => {
                        const created = sr.CreatedAt
                          ? new Date(sr.CreatedAt).toLocaleString()
                          : 'Unknown date';

                        const count = sr.Recommendations?.length ?? 0;

                        return (
                          <div
                            key={sr.SearchId}
                            className="business-card-wrapper"
                            onClick={() => {
                              navigate('/ai-service-recommendations', {
                                state: {
                                  recommendations: sr.Recommendations ?? [],
                                  customerDemand: sr.CustomerDemand ?? '',
                                },
                              });
                            }}
                          >
                            <div className="recommendation-history-card">
                              <div className="recommendation-history-row">
                                <Typography
                                  variant="subtitle1"
                                  className="recommendation-history-title"
                                >
                                  Search on {created}
                                </Typography>
                              </div>
                              <div className="recommendation-history-meta">
                                <span className="recommendation-history-count">
                                  {count} recommendation{count === 1 ? '' : 's'}
                                </span>
                              </div>
                              {sr.CustomerDemand ? (
                                <Typography
                                  variant="body2"
                                  className="recommendation-history-request"
                                >
                                  <strong>Request:</strong> {sr.CustomerDemand}
                                </Typography>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <Alert severity="info">
                        You don&apos;t have any search history yet. Use Smart Search to find a service
                        pro, and your results will be saved here so you can revisit them anytime.
                      </Alert>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {userData?.Role === 'user' ? (
            <div className="danger-zone-section">
              <Typography variant="h6" gutterBottom>
                Delete account
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Permanently delete your profile and data from NKY Connect. This cannot be undone.
              </Typography>
              <DangerButton onClick={handleOpenDeleteAccount}>
                Delete account
              </DangerButton>
            </div>
          ) : (
            <div className="danger-zone-section">
              <Typography variant="h6" gutterBottom>
                Account removal
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Business and admin accounts cannot be deleted from this page. Contact support if you
                need to close your account.
              </Typography>
              {userData?.Role === 'business_user' ? (
                <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                  If you meant to cancel your subscription instead of removing your account, you can
                  manage that from your{' '}
                  <Link component={RouterLink} to="/dashboard" fontWeight={600}>
                    business dashboard
                  </Link>
                  .
                </Alert>
              ) : null}
              <SecondaryButton
                component="a"
                href={teamContactMailtoHref({
                  subject: 'NKY Connect — account or subscription help',
                })}
              >
                Contact us
              </SecondaryButton>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <StyledDialog
        open={confirmDialog.open}
        onClose={handleCancelRemove}
        aria-labelledby="remove-provider-dialog"
      >
        <div className="dialog-title">Remove Saved Business</div>
        <div className="dialog-content">
          <Typography>
            Are you sure that you want to remove {confirmDialog.businessName}{' '}
            from your Saved Businesses?
          </Typography>
        </div>
        <div className="dialog-actions">
          <SecondaryButton onClick={handleCancelRemove} disabled={removeSaving}>
            Cancel
          </SecondaryButton>
          <DangerButton onClick={handleConfirmRemove} disabled={removeSaving}>
            {removeSaving ? 'Removing…' : 'Remove'}
          </DangerButton>
        </div>
      </StyledDialog>

      <DeleteAccountWarningDialog
        open={deleteAccountDialogOpen}
        onClose={handleCloseDeleteAccount}
        aria-labelledby="delete-account-dialog-title"
        aria-describedby="delete-account-dialog-desc"
      >
        <div className="deleteWarnHeader">
          <WarningAmberRounded
            sx={{
              fontSize: 40,
              color: '#d97706',
              flexShrink: 0,
              filter: 'drop-shadow(0 1px 2px rgba(180, 83, 9, 0.25))',
            }}
            aria-hidden
          />
          <div>
            <div className="deleteWarnTitle" id="delete-account-dialog-title">
              Warning: permanent account deletion
            </div>
            <div className="deleteWarnSubtitle">
              You are about to permanently erase your NKY Connect profile. This cannot be reversed.
            </div>
          </div>
        </div>
        <div className="deleteWarnBody" id="delete-account-dialog-desc">
          <Alert severity="warning" variant="outlined" sx={{ mb: deleteAccountError ? 2 : 0 }}>
            <Typography variant="body2" component="span" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
              Please confirm you understand the consequences:
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 0 }}>
              • Your account and profile details will be removed.
              <br />
              • Saved businesses and history tied to this login may no longer be available.
              <br />• You will be signed out immediately after deletion.
            </Typography>
          </Alert>
          {deleteAccountError ? (
            <Alert severity="error">{deleteAccountError}</Alert>
          ) : null}
        </div>
        <div className="dialog-actions">
          <SecondaryButton
            onClick={handleCloseDeleteAccount}
            disabled={deleteAccountLoading}
          >
            Cancel
          </SecondaryButton>
          <DangerButton
            onClick={handleConfirmDeleteAccount}
            disabled={deleteAccountLoading}
            startIcon={
              deleteAccountLoading ? (
                <NkySpinner size="sm" onDark />
              ) : null
            }
          >
            {deleteAccountLoading ? 'Deleting…' : 'Yes, delete my account'}
          </DangerButton>
        </div>
      </DeleteAccountWarningDialog>

      <WizardRequestSummaryDialog
        open={Boolean(selectedWizardRequest)}
        onClose={handleCloseWizardRequestSummary}
        wizardState={
          selectedWizardRequest
            ? {
              serviceType: selectedWizardRequest.serviceType,
              subServiceType: selectedWizardRequest.subServiceType ?? undefined,
              inquiryKind: selectedWizardRequest.inquiryKind,
              targetServiceTitle: selectedWizardRequest.targetServiceTitle,
              formData: selectedWizardRequest.formData,
              requestSummary: selectedWizardRequest.requestSummary,
            }
            : null
        }
        serviceLabel={
          selectedWizardRequest
            ? getServiceRequestDisplayNames(
              selectedWizardRequest.serviceType,
              selectedWizardRequest.subServiceType,
              selectedWizardRequest.targetServiceTitle,
              selectedWizardRequest.inquiryKind,
            ).tradeName
            : ''
        }
        subserviceLabel={
          selectedWizardRequest
            ? getServiceRequestDisplayNames(
              selectedWizardRequest.serviceType,
              selectedWizardRequest.subServiceType,
              selectedWizardRequest.targetServiceTitle,
              selectedWizardRequest.inquiryKind,
            ).subserviceName
            : ''
        }
        urgencyLabel={selectedWizardRequest?.requestSummary?.urgency}
        leads={selectedWizardRequest?.leads ?? []}
        businessNameById={businessNameById}
        helpRequested={Boolean(selectedWizardRequest?.helpRequested)}
      />

      <CustomServiceRequestDetailDialog
        open={Boolean(selectedCustomRequest)}
        onClose={handleCloseCustomRequestDetail}
        requestRow={selectedCustomRequest}
        businessNameById={businessNameById}
      />

      <NkySnackbarAlert
        open={saveToast.open}
        onClose={closeSaveToast}
        severity={saveToast.severity}
        message={saveToast.message}
      />
    </AccountPageWrapper>
  );
}

export default AccountPage;
